import {
  ebayCategoryPolicyReviewIdentity,
  isEbayCategoryPolicyFresh,
  requireEbayCcgPublicPolicy,
  validateEbayCcgPublicSpecifics,
  mapEbayCategoryPresentation,
  ebayCategoryPresentationLabel,
  type CardPresentation,
  type EbayCategoryPolicy,
  type EbayItemSpecifics,
  type GetEbayCategoryPolicyResponse,
  type MarketplaceCcgAction,
  type MarketplaceCcgReviewRequest,
  type MarketplaceListingReadinessRecord,
  type EbaySellerHubCategorySelection,
  type EbaySellerHubSinglesCategory,
} from '@tcvs/shared';
import type { ApiFailure } from '../auth/api-client';
export type CcgPolicyState =
  | { status: 'UNREQUESTED' | 'LOADING' | 'UNAVAILABLE' | 'EXPIRED' }
  | {
      status: 'AVAILABLE';
      policy: EbayCategoryPolicy;
      reviewIdentity: string;
      limits: { ccgExportCards: 19 };
    };
export type CcgFieldDecision = {
  value: string;
  origin: 'MANUAL_REVIEW' | 'IMPORTED_RESEARCH';
  confirmed: boolean;
};
export interface CcgLocalReview {
  cardId: string;
  contextKey: string;
  policyReviewIdentity: string;
  detailsConfirmed: boolean;
  specifics: Partial<Record<'Finish' | 'Rarity', CcgFieldDecision>>;
}
const object = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);
function keys(v: unknown, allowed: readonly string[]): void {
  if (!object(v) || Object.keys(v).some((k) => !allowed.includes(k)))
    throw new Error('Invalid public CCG policy response.');
}
/** No private dependency dictionary, review provenance, source evidence or caller capability. */
export function parseCcgPolicyResponse(
  value: unknown,
  nowMs = Date.now(),
): GetEbayCategoryPolicyResponse {
  keys(value, ['category', 'policy', 'limits']);
  const v = value as GetEbayCategoryPolicyResponse;
  if (
    new TextEncoder().encode(JSON.stringify(v)).length > 65 * 1024 ||
    v.category !== 'CCG_SINGLES'
  )
    throw new Error('Invalid public CCG policy response.');
  keys(v.limits, ['ccgExportCards']);
  if (v.limits.ccgExportCards !== 19) throw new Error('Unsupported CCG export limit.');
  keys(v.policy, ['binding', 'aspects', 'conditions', 'evidence']);
  keys(v.policy.binding, [
    'schemaVersion',
    'parserVersion',
    'environment',
    'marketplace',
    'locale',
    'categoryId',
    'categoryTreeId',
    'categoryTreeVersion',
    'semanticDigest',
    'rawSha256',
    'templateSha256',
    'templateHeadersSha256',
    'fetchedAt',
    'expiresAt',
  ]);
  keys(v.policy.binding.rawSha256, ['tree', 'aspects', 'conditions']);
  if (!Array.isArray(v.policy.aspects)) throw new Error('Invalid CCG aspects.');
  for (const a of v.policy.aspects)
    keys(a, [
      'name',
      'dataType',
      'format',
      'cardinality',
      'mode',
      'required',
      'usage',
      'enabledForVariations',
      'applicableTo',
      'maxLength',
      'selectionValues',
      'suggestions',
      'suggestionsTruncated',
      'hasValueDependencies',
    ]);
  keys(v.policy.conditions, [
    'raw',
    'graded',
    'certificationMaxLength',
    'certificationRequired',
    'gradedConditionId',
    'rawConditionId',
  ]);
  if (!Array.isArray(v.policy.conditions.raw) || !Array.isArray(v.policy.conditions.graded))
    throw new Error('Invalid CCG conditions.');
  for (const r of v.policy.conditions.raw) keys(r, ['condition', 'valueId', 'label']);
  for (const g of v.policy.conditions.graded) {
    keys(g, ['grader', 'valueId', 'grades']);
    if (!Array.isArray(g.grades)) throw new Error('Invalid CCG grades.');
    for (const n of g.grades) keys(n, ['grade', 'valueId']);
  }
  requireEbayCcgPublicPolicy(v.policy);
  if (!isEbayCategoryPolicyFresh(v.policy.binding, nowMs))
    throw new Error('CCG policy expired. Reload the policy.');
  return v;
}
export function ccgReviewScopeKey(input: {
  cardId: string;
  contextKey: string;
  policyReviewIdentity: string;
  specifics: EbayItemSpecifics;
  presentation?: CardPresentation;
  templateId: string;
}): string {
  return JSON.stringify(input);
}
export function validateCcgLocalSpecifics(
  policy: EbayCategoryPolicy,
  values: EbayItemSpecifics,
): string[] {
  const errors = validateEbayCcgPublicSpecifics(policy, values);
  if (new TextEncoder().encode(JSON.stringify(values)).length > 32 * 1024)
    errors.push('CCG item details exceed the save size limit.');
  return errors;
}
export function ccgPresentationCompatibility(
  policy: EbayCategoryPolicy,
  presentation: CardPresentation | undefined,
): { ready: true; label: string } | { ready: false; message: string } {
  try {
    mapEbayCategoryPresentation('CCG_SINGLES', presentation, policy);
    return {
      ready: true,
      label: ebayCategoryPresentationLabel('CCG_SINGLES', presentation, policy),
    };
  } catch (e) {
    return {
      ready: false,
      message: `${e instanceof Error ? e.message : 'Unsupported CCG condition.'} The internal draft can still be saved unchanged.`,
    };
  }
}
/** Only explicit final Save/Ready supplies detailsConfirmed; loading never creates this intent. */
export function serializeCcgReview(
  review: CcgLocalReview,
  values: EbayItemSpecifics,
  policy: EbayCategoryPolicy,
  currentScopeKey: string,
): MarketplaceCcgReviewRequest {
  requireEbayCcgPublicPolicy(policy);
  if (
    !review.detailsConfirmed ||
    review.contextKey !== currentScopeKey ||
    review.policyReviewIdentity !== ebayCategoryPolicyReviewIdentity(policy.binding)
  )
    throw new Error('Review the current CCG details again.');
  const errors = validateCcgLocalSpecifics(policy, values);
  if (errors.length) throw new Error(errors.join(' '));
  const specifics: MarketplaceCcgReviewRequest['specifics'] = {};
  for (const name of ['Finish', 'Rarity'] as const) {
    const value = values[name];
    if (value === undefined) continue;
    const d = review.specifics[name];
    if (
      value.length !== 1 ||
      !d?.confirmed ||
      d.value !== value[0] ||
      !['MANUAL_REVIEW', 'IMPORTED_RESEARCH'].includes(d.origin)
    )
      throw new Error(`Review ${name} again.`);
    specifics[name] = { origin: d.origin, value: d.value };
  }
  return { schemaVersion: 1, policyReviewIdentity: review.policyReviewIdentity, specifics };
}
export function ccgWorkflowFailure(
  result: ApiFailure,
): { message: string; action: MarketplaceCcgAction } | undefined {
  const b = result.body;
  if (
    !object(b) ||
    ![
      'REVIEW_AND_SAVE_AGAIN',
      'RELOAD_POLICY',
      'TRY_AGAIN',
      'CHOOSE_FEWER_CARDS',
      'CHECK_STATUS',
    ].includes(String(b.action))
  )
    return undefined;
  return {
    message: typeof b.message === 'string' ? b.message : (result.message ?? result.error),
    action: b.action as MarketplaceCcgAction,
  };
}
/** Exact known labels only. A label never selects CCG or supplies Game. */
export function listingCategoryForLabel(
  label: string | undefined,
): EbaySellerHubSinglesCategory | null {
  const text = label?.trim().toLocaleLowerCase('en-US');
  if (['non-sport', 'non sport', 'nonsport', 'non-sports', 'non sports'].includes(text ?? ''))
    return 'NON_SPORT_SINGLES';
  if (
    [
      'baseball',
      'basketball',
      'football',
      'hockey',
      'soccer',
      'wrestling',
      'racing',
      'golf',
      'sport',
      'sports',
    ].includes(text ?? '')
  )
    return 'SPORT_SINGLES';
  return null;
}
export function resolveSellerHubUiSelection(
  items: readonly MarketplaceListingReadinessRecord[],
  selectedIds: readonly string[],
  category: EbaySellerHubCategorySelection,
):
  | { kind: 'LEGACY'; cardIds: string[] }
  | { kind: 'CCG'; cardIds: string[]; limit: 19 }
  | { kind: 'BLOCKED'; message: string } {
  const cardIds = [...new Set(selectedIds)];
  const categories = cardIds.map((id) => {
    const item = items.find((v) => v.cardId === id);
    if (!item) return undefined;
    const hasCategory = Object.hasOwn(item, 'itemSpecificsCategory'),
      hasSnapshot = Object.hasOwn(item, 'exportSnapshot'),
      hasPolicy = Object.hasOwn(item, 'ccgPolicy');
    if (!hasCategory && !hasSnapshot && !hasPolicy)
      return listingCategoryForLabel(item.identity?.category) ?? undefined;
    const values: unknown[] = [];
    if (hasCategory) values.push(item.itemSpecificsCategory);
    if (hasSnapshot) {
      const snap = item.exportSnapshot;
      if (!snap || ![1, 2].includes(snap.schemaVersion)) return undefined;
      values.push(snap.category);
    }
    if (hasPolicy) {
      const policy = item.ccgPolicy;
      if (
        !policy ||
        policy.binding?.categoryId !== '183454' ||
        policy.reviewIdentity !== ebayCategoryPolicyReviewIdentity(policy.binding)
      )
        return undefined;
      values.push('CCG_SINGLES');
    }
    if (
      values.some(
        (v) => !['SPORT_SINGLES', 'NON_SPORT_SINGLES', 'CCG_SINGLES'].includes(String(v)),
      ) ||
      new Set(values).size !== 1
    )
      return undefined;
    return values[0] as EbaySellerHubSinglesCategory;
  });
  if (categories.some((v) => v === undefined))
    return {
      kind: 'BLOCKED',
      message: 'Choose reviewed, consistent listing categories before export.',
    };
  const ccg = categories.includes('CCG_SINGLES') || category === 'CCG_SINGLES';
  if (ccg) {
    if (
      categories.some((v) => v !== 'CCG_SINGLES') ||
      (category !== 'AUTO' && category !== 'CCG_SINGLES')
    )
      return { kind: 'BLOCKED', message: 'Select only reviewed CCG cards for this export.' };
    if (cardIds.length > 19)
      return {
        kind: 'BLOCKED',
        message: 'Select at most 19 CCG cards. CCG exports create one CSV file.',
      };
    return { kind: 'CCG', cardIds, limit: 19 };
  }
  // Legacy records with an explicit legacy filter preserve the prior reviewed export path.
  if (category === 'AUTO' && categories.some((v) => v === undefined))
    return { kind: 'BLOCKED', message: 'Choose a reviewed listing category before export.' };
  return { kind: 'LEGACY', cardIds };
}
